<script lang="ts">
  /**
   * Post-effects. Without `layer`: the overall effects, on the finished picture. With `layer`:
   * each effect has an "All layers / This layer" switch; "This layer" applies it to this layer
   * only, before it's blended with the others.
   */
  import type { AppController } from '../../app/controller.svelte';
  import { POSTFX } from '../../shared/postfx';
  import type { Layer } from '../../shared/scene';
  import ParamControl from './ParamControl.svelte';

  interface Props {
    ctl: AppController;
    layer?: Layer;
    layerIndex?: number;
  }
  let { ctl, layer, layerIndex = 0 }: Props = $props();
  const scene = $derived(ctl.scene);
</script>

{#each POSTFX as fx (fx.id)}
  {@const local = !!layer?.postfx?.[fx.id]}
  <details class="fx" data-testid={layer ? `layer-fx-${fx.id}` : `fx-${fx.id}`}>
    <summary>
      <span class="grow">{fx.name}</span>
      {#if local}<span class="chip accent">this layer</span>{/if}
    </summary>
    {#if layer}
      <div class="seg" role="group" aria-label={`${fx.name} applies to`}>
        <button
          class:active={!local}
          aria-pressed={!local}
          data-testid={`scope-all-${fx.id}`}
          onclick={() => local && ctl.setEffectScope(layer.id, fx.id, 'all')}>All layers</button
        >
        <button
          class:active={local}
          aria-pressed={local}
          data-testid={`scope-layer-${fx.id}`}
          onclick={() => !local && ctl.setEffectScope(layer.id, fx.id, 'layer')}
          >This layer only</button
        >
      </div>
    {/if}
    {#each fx.params as p (p.id)}
      {#if layer && local}
        <ParamControl
          {ctl}
          def={p}
          value={layer.postfx?.[fx.id]?.[p.id] ?? p.default}
          target={`layer/${layer.id}/fx/${fx.id}/${p.id}`}
          midiPath={`layer#${layerIndex}/fx/${fx.id}/${p.id}`}
          onchange={(v) =>
            ctl.dispatch(
              { type: 'layer.fx.param', layerId: layer.id, fxId: fx.id, paramId: p.id, value: v },
              `${layer.id}/fx/${fx.id}/${p.id}`,
            )}
        />
      {:else}
        <ParamControl
          {ctl}
          def={p}
          value={scene.postfx[fx.id]?.[p.id] ?? p.default}
          target={`fx/${fx.id}/${p.id}`}
          midiPath={`fx/${fx.id}/${p.id}`}
          onchange={(v) =>
            ctl.dispatch(
              { type: 'fx.param', fxId: fx.id, paramId: p.id, value: v },
              `fx/${fx.id}/${p.id}`,
            )}
        />
      {/if}
    {/each}
    <div class="row end">
      <button
        class="link"
        onclick={() =>
          layer && local
            ? ctl.setEffectScope(layer.id, fx.id, 'all')
            : ctl.dispatch({ type: 'fx.reset', fxId: fx.id })}
        >{layer && local ? 'Remove from this layer' : 'Reset'}</button
      >
    </div>
  </details>
{/each}

<style>
  .fx {
    border-top: 1px solid var(--border);
    padding: 2px 0;
  }
  .fx summary {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 6px 0;
    cursor: pointer;
    font-size: 12.5px;
    color: var(--muted);
  }
  .fx[open] summary {
    color: var(--text);
  }
  .seg {
    display: flex;
    gap: 4px;
    margin: 2px 0 8px;
  }
  .seg button {
    flex: 1;
    font-size: 11.5px;
    padding: 3px 4px;
  }
  .end {
    justify-content: flex-end;
  }
</style>
